import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


import { FolderPlus, Building, Wrench, FileText, Upload, Zap, User, Search, Plus, Trash2 } from "lucide-react";
import { FileInput } from "@/components/ui/file-input";

import { useClientes } from "@/hooks/useClientes";
import { useEquipamentos } from "@/hooks/useEquipamentos";
import { useInstaladores } from "@/hooks/useInstaladores";
import { InstaladorSearchSelect } from "@/components/InstaladorSearchSelect";
import { ConsultorSearchSelect } from "@/components/ConsultorSearchSelect";
import { useCriarOrcamento } from "@/hooks/useOrcamentos";
import { useAuth } from "@/hooks/useAuth";

import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { SelecionarPlanoDialog } from "@/components/SelecionarPlanoDialog";
import { ClienteSearchSelect } from "@/components/ClienteSearchSelect";
import { toast } from "sonner";
import { useEmpresasResponsaveis, type EmpresaResponsavel } from "@/hooks/useEmpresasResponsaveis";
import { EmpresaSearchSelect } from "@/components/EmpresaSearchSelect";
import { uploadFileToStorage } from "@/lib/storageUrl";
import { MAX_OBSERVACOES_PROPOSTA } from "@/config/coordenadasPDF";

interface EquipamentoSelecionado {
  tipo: string;
  equipamento_id: string;
  quantidade: number;
  equipamento?: {
    id: string;
    marca: string;
    modelo: string;
    tipo: string;
    potencia?: string;
  };
}

interface NovoProjetoProps {
  onSuccess?: () => void;
}

// Função para formatar valor como moeda brasileira
const formatarMoeda = (valor: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor);
};

// Função para aplicar máscara de moeda durante digitação
const aplicarMascaraMoeda = (valor: string): string => {
  // Remove tudo que não é número
  let numeros = valor.replace(/\D/g, '');
  
  // Se não há números, retorna R$ 0,00
  if (numeros === '') return 'R$ 0,00';
  
  // Converte para número e divide por 100 para ter centavos
  const valorNumerico = parseInt(numeros) / 100;
  
  // Formata usando Intl.NumberFormat
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valorNumerico);
};

// Função para converter string formatada para número
const converterMoedaParaNumero = (valorFormatado: string): number => {
  // Remove R$, espaços e converte vírgula para ponto
  const valorLimpo = valorFormatado
    .replace(/R\$\s*/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
    .trim();
  
  return parseFloat(valorLimpo) || 0;
};

// Função para aplicar máscara de números apenas
const aplicarMascaraNumeros = (valor: string): string => {
  // Remove tudo que não é número
  return valor.replace(/\D/g, '');
};

export function NovoProjeto({ onSuccess }: NovoProjetoProps = {}) {
  const { data: clientes = [] } = useClientes();
  const { data: equipamentos = [] } = useEquipamentos();
  const { data: instaladores = [] } = useInstaladores();
  const { data: empresas = [] } = useEmpresasResponsaveis();
  const { user, profile: currentUser } = useAuth();
  const criarOrcamento = useCriarOrcamento();
  const queryClient = useQueryClient();
  
  // Estado para controlar loading e prevenir duplo clique
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const isCreatingRef = useRef(false);
  const empresaInputRef = useRef<HTMLInputElement | null>(null);
  
  const [novoProjeto, setNovoProjeto] = useState({
    cliente_id: null as number | null,
    observacoes: "",
    valor_projeto: 0,
    
    // Dados da empresa responsável
    empresa_responsavel_id: null as number | null,
    empresa_responsavel: "",
    cnpj_empresa: "",
    telefone_empresa: "",
    email_empresa: "",
    is_projeto_elevo: false,
    id_projeto_elevo: "",
    empresa_projeto_externo_id: "",
    
    // Consultor responsável
    consultor_id: "",
    consultor_nome: "",
    consultor_celular: "",
    consultor_cpf_cnpj: "",
    consultor_email: "",

    // Dados do instalador
    instalador_id: "",
    instalador_nome: "",
    instalador_cpf_cnpj: "",
    instalador_contato: "",
    
    // Dados do vendedor (novo)
    vendedor_nome: "",
    vendedor_celular: "",
    vendedor_cpf_cnpj: "",
    
    // Arquivos
    vistoria_file: null as File | null
  });

  const [equipamentosSelecionados, setEquipamentosSelecionados] = useState<EquipamentoSelecionado[]>([]);
  const [novoEquipamento, setNovoEquipamento] = useState({
    tipo: "",
    equipamento_id: "",
    quantidade: 1
  });

  const [showSelecionarPlano, setShowSelecionarPlano] = useState(false);
  const [projetoParaOrcamento, setProjetoParaOrcamento] = useState<any>(null);

  // Preencher dados do vendedor automaticamente quando o usuário estiver disponível
  useEffect(() => {
    if (currentUser && !novoProjeto.vendedor_nome) {
      setNovoProjeto(prev => ({
        ...prev,
        vendedor_nome: currentUser.nome || "",
        vendedor_celular: currentUser.telefone || "",
        vendedor_cpf_cnpj: "" // CPF/CNPJ não está no perfil, deixar vazio
      }));
    }
  }, [currentUser]);

  // Verificar se há um cliente pré-selecionado no localStorage
  useEffect(() => {
    const clienteParaProjeto = localStorage.getItem('clienteParaProjeto');
    if (clienteParaProjeto) {
      try {
        const cliente = JSON.parse(clienteParaProjeto);
        setNovoProjeto(prev => ({
          ...prev,
          cliente_id: cliente.id
        }));
        
        // Limpar o localStorage após usar
        localStorage.removeItem('clienteParaProjeto');
        
        toast.success(`Cliente pré-selecionado: ${cliente.nome}`);
        return;
      } catch (error) {
        console.error('Erro ao processar cliente pré-selecionado:', error);
        localStorage.removeItem('clienteParaProjeto');
      }
    }

    // Fallback: último cliente cadastrado pelo usuário
    const ultimoCliente = localStorage.getItem('ultimoClienteCadastrado');
    if (ultimoCliente) {
      try {
        const cliente = JSON.parse(ultimoCliente);
        if (cliente?.id) {
          setNovoProjeto(prev => ({
            ...prev,
            cliente_id: cliente.id
          }));
          toast.success(`Cliente pré-selecionado: ${cliente.nome || `#${cliente.id}`}`);
        }
      } catch (error) {
        console.error('Erro ao processar último cliente cadastrado:', error);
        localStorage.removeItem('ultimoClienteCadastrado');
      }
    }
  }, []);

  const isEmpresaElevo = (empresaNome?: string | null) => {
    const n = (empresaNome || '').toLowerCase();
    return n.includes('elevo');
  };

  const aplicarEmpresaDoCadastro = (empresa: EmpresaResponsavel) => {
    const elevo = isEmpresaElevo(empresa.nome);
    setNovoProjeto(prev => ({
      ...prev,
      empresa_responsavel_id: empresa.id,
      empresa_responsavel: empresa.nome || "",
      cnpj_empresa: empresa.cnpj || "",
      telefone_empresa: empresa.telefone || "",
      email_empresa: empresa.email || "",
      is_projeto_elevo: elevo,
      // Compatibilidade: manter campo legado preenchido quando a empresa for ELEVO
      id_projeto_elevo: elevo ? (prev.empresa_projeto_externo_id || prev.id_projeto_elevo || "") : "",
    }));
  };

  const preencherDadosElevo = () => {
    const elevo = empresas.find(e => isEmpresaElevo(e.nome));
    if (elevo) {
      aplicarEmpresaDoCadastro(elevo);
      toast.success("Empresa ELEVO selecionada a partir do cadastro!");
      return;
    }

    // Fallback para manter o fluxo antigo caso ELEVO não esteja cadastrada ainda
    setNovoProjeto(prev => ({
      ...prev,
      empresa_responsavel_id: null,
      is_projeto_elevo: true,
      empresa_responsavel: "ELEVO ENERGY",
      cnpj_empresa: "35.325.068/0001-25",
      telefone_empresa: "44 3367-8689",
      email_empresa: "felipe.rosa@elevoenergy.com.br"
    }));
    toast.success("Dados da Elevo Energy preenchidos automaticamente (fallback)!");
  };

  // Função para fazer upload de arquivos
  const uploadFile = async (file: File, tipo: string, projetoId: number) => {
    try {
      console.log(`Iniciando upload do arquivo ${tipo} para projeto ${projetoId}`);
      
      // Verificar se o arquivo existe
      if (!file) {
        throw new Error('Arquivo não fornecido');
      }

      // Verificar tamanho do arquivo (máximo 20MB)
      const maxSize = 20 * 1024 * 1024; // 20MB
      if (file.size > maxSize) {
        throw new Error('Arquivo muito grande. Máximo 20MB permitido.');
      }

      const timestamp = Date.now();
      const fileName = `${tipo}_${projetoId}_${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      const filePath = `projetos/${projetoId}/${fileName}`;
      const { publicUrl } = await uploadFileToStorage(filePath, file);

      if (publicUrl) {
        console.log(`Atualizando projeto ${projetoId} com informações do arquivo`);
        
        // Atualizar projeto com informações do arquivo
        const updateData: any = {};
        updateData[`${tipo}_file_url`] = publicUrl;
        updateData[`${tipo}_file_name`] = file.name;
        
        const { error: updateError } = await supabase
          .from('projetos')
          .update(updateData)
          .eq('id', projetoId);
          
        if (updateError) {
          console.error('Erro ao atualizar projeto:', updateError);
          throw new Error(`Erro ao atualizar projeto: ${updateError.message}`);
        }
        
        console.log(`Upload e atualização concluídos com sucesso`);
        return true;
      }

      throw new Error("Erro no upload: URL não gerada");
    } catch (error) {
      console.error(`Erro ao fazer upload do arquivo ${tipo}:`, error);
      return false;
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>, tipo: 'vistoria') => {
    const file = e.target.files?.[0];
    if (file) {
      setNovoProjeto({...novoProjeto, vistoria_file: file});
      toast.success('Vistoria anexada com sucesso!');
    }
  };

  const adicionarEquipamento = () => {
    if (!novoEquipamento.tipo || !novoEquipamento.equipamento_id) {
      toast.error("Selecione o tipo e o equipamento");
      return;
    }

    const equipamento = equipamentos.find(eq => eq.id.toString() === novoEquipamento.equipamento_id);
    if (!equipamento) return;

    const novoItem: EquipamentoSelecionado = {
      tipo: novoEquipamento.tipo,
      equipamento_id: novoEquipamento.equipamento_id,
      quantidade: novoEquipamento.quantidade,
      equipamento: {
        id: equipamento.id.toString(),
        marca: equipamento.marca,
        modelo: equipamento.modelo,
        tipo: equipamento.tipo,
        potencia: equipamento.potencia || undefined
      }
    };

    setEquipamentosSelecionados([...equipamentosSelecionados, novoItem]);
    setNovoEquipamento({ tipo: "", equipamento_id: "", quantidade: 1 });
    toast.success("Equipamento adicionado!");
  };

  const removerEquipamento = (index: number) => {
    const novosEquipamentos = equipamentosSelecionados.filter((_, i) => i !== index);
    setEquipamentosSelecionados(novosEquipamentos);
    toast.success("Equipamento removido!");
  };

  const confirmarPlano = async (planoData: {
    tipo_plano: string;
    vigencia_anos: number;
    quantidade_placas: number;
    valor_total: number;
    valor_tabela: number;
    desconto_valor: number;
    forma_pagamento_id: string;
    parcelas_cartao: number;
  }) => {
    if (!projetoParaOrcamento) {
      console.error('projetoParaOrcamento não está definido');
      toast.error('Erro: dados do projeto não encontrados');
      return;
    }

    console.log('Confirmando plano:', planoData);
    console.log('Dados do projeto:', projetoParaOrcamento);

    try {
      // Criar orçamento com os dados do plano selecionado
      // Validações adicionais
      if (!planoData.valor_total || planoData.valor_total <= 0) {
        console.error('Valor total inválido:', planoData.valor_total);
        toast.error('Valor total deve ser maior que zero');
        return;
      }

      const clienteId =
        projetoParaOrcamento.cliente_id ?? projetoParaOrcamento.projeto?.cliente_id;
      if (!clienteId) {
        console.error('cliente_id não encontrado');
        toast.error('ID do cliente não encontrado');
        return;
      }

      // Buscar o plano selecionado para obter o valor por painel
      const { data: planoSelecionado, error: planoError } = await supabase
        .from('planos_seguro')
        .select('valor_por_painel')
        .eq('id', planoData.tipo_plano)
        .single();

      if (planoError) {
        console.error('Erro ao buscar plano:', planoError);
        toast.error('Erro ao buscar dados do plano selecionado');
        return;
      }

      // Valor do seguro = valor final da proposta (já com desconto comercial, se houver)
      const valorSeguro = planoData.valor_total;
      const valorTabela = Number(planoData.valor_tabela) || valorSeguro;
      const descontoValor = Math.max(
        0,
        Number(planoData.desconto_valor) || Math.round((valorTabela - valorSeguro) * 100) / 100
      );
      
      const valorProjeto = projetoParaOrcamento.projeto.valor_projeto;
      const valorTotalOrcamento =
        valorProjeto && valorProjeto > 0 ? valorProjeto : planoData.valor_total;

      const orcamentoData = {
        cliente_id: clienteId,
        projeto_id: projetoParaOrcamento.projeto.id,
        valor_total: valorTotalOrcamento,
        plano_id: planoData.tipo_plano,
        quantidade_placas: planoData.quantidade_placas,
        valor_seguro: valorSeguro,
        vigencia_anos: planoData.vigencia_anos,
        forma_pagamento: planoData.forma_pagamento_id,
        parcelas_cartao: planoData.parcelas_cartao,
        numero_parcelas: planoData.parcelas_cartao,
        valor_tabela: valorTabela,
        desconto_valor: descontoValor,
        observacoes: (() => {
          const obsUsuario = (
            projetoParaOrcamento.projeto?.observacoes ||
            ''
          ).trim();
          const descontoTxt =
            descontoValor > 0
              ? ` Desconto comercial: R$ ${descontoValor.toFixed(2)} (tabela R$ ${valorTabela.toFixed(2)} → final R$ ${valorSeguro.toFixed(2)}).`
              : '';
          const meta = `Orçamento gerado do projeto ${projetoParaOrcamento.projeto.id}. Plano: ${planoData.tipo_plano}, Vigência: ${planoData.vigencia_anos} anos, Quantidade de painéis: ${planoData.quantidade_placas}.${descontoTxt} Equipamentos: ${projetoParaOrcamento.equipamentos.map(eq => `${eq.equipamento?.marca} ${eq.equipamento?.modelo} (${eq.quantidade}x)`).join(', ')}`;
          return obsUsuario || meta;
        })(),
      };

      console.log('Dados para criar orçamento:', orcamentoData);

      try {
        const orcamentoCriado = await criarOrcamento.mutateAsync(orcamentoData);
        console.log('✅ Orçamento criado com sucesso:', orcamentoCriado);
        console.log('📊 Dados do orçamento criado:', {
          id: orcamentoCriado.id,
          valor_total: orcamentoCriado.valor_total,
          valor_seguro: orcamentoCriado.valor_seguro,
          plano_id: orcamentoCriado.plano_id,
          quantidade_placas: orcamentoCriado.quantidade_placas,
          vigencia_anos: orcamentoCriado.vigencia_anos
        });
        
        // Atualizar o projeto com o ID do orçamento criado
        // Não grava orcamento_id no projeto na criação: só após aprovação
        // (um projeto pode ter vários orçamentos; só o aprovado vira o vínculo oficial).
        console.log('Orçamento criado; vínculo oficial no projeto só após aprovação:', orcamentoCriado.id);
      } catch (error) {
        console.error('Erro detalhado ao criar orçamento:', error);
        throw error;
      }

      // Invalidar queries para atualizar a listagem
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      
      toast.success("Projeto salvo e orçamento gerado com sucesso!");
      
      // Redirecionar automaticamente para a listagem de orçamentos
      setIsCreatingProject(false);
      isCreatingRef.current = false;
      window.dispatchEvent(new CustomEvent('mudar_secao', { detail: 'orcamentos' }));

      // Reset form
      setNovoProjeto({
        cliente_id: null,
        observacoes: "",
        valor_projeto: 0,
        empresa_responsavel_id: null,
        empresa_responsavel: "",
        cnpj_empresa: "",
        telefone_empresa: "",
        email_empresa: "",
        is_projeto_elevo: false,
        id_projeto_elevo: "",
        empresa_projeto_externo_id: "",
        consultor_id: "",
        consultor_nome: "",
        consultor_celular: "",
        consultor_cpf_cnpj: "",
        consultor_email: "",
        instalador_id: "",
        instalador_nome: "",
        instalador_cpf_cnpj: "",
        instalador_contato: "",
        vendedor_nome: "",
        vendedor_celular: "",
        vendedor_cpf_cnpj: "",
        vistoria_file: null
      });
      setEquipamentosSelecionados([]);

    } catch (error) {
      console.error('Erro ao gerar orçamento:', error);
      toast.error("Erro ao gerar orçamento. Tente novamente.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!novoProjeto.cliente_id) {
      toast.error("Selecione um cliente");
      return;
    }

    try {
      console.log('Cadastrando projeto (apenas projeto):', novoProjeto);
      
      // Primeiro, criar o projeto
      const projetoPayload: Record<string, unknown> = {
        cliente_id: novoProjeto.cliente_id,
        status: "orcamento", // Status padrão
        observacoes: novoProjeto.observacoes,
        valor_projeto: novoProjeto.valor_projeto,
        empresa_responsavel_id: novoProjeto.empresa_responsavel_id,
        empresa_projeto_externo_id: novoProjeto.empresa_projeto_externo_id || null,
        empresa_responsavel: novoProjeto.empresa_responsavel,
        cnpj_empresa: novoProjeto.cnpj_empresa,
        telefone_empresa: novoProjeto.telefone_empresa,
        email_empresa: novoProjeto.email_empresa,
        is_projeto_elevo: novoProjeto.is_projeto_elevo,
        id_projeto_elevo: novoProjeto.id_projeto_elevo,
        consultor_id: novoProjeto.consultor_id || null,
        consultor_nome: novoProjeto.consultor_nome || null,
        consultor_celular: novoProjeto.consultor_celular || null,
        consultor_cpf_cnpj: novoProjeto.consultor_cpf_cnpj || null,
        consultor_email: novoProjeto.consultor_email || null,
        instalador_nome: novoProjeto.instalador_nome,
        instalador_cpf_cnpj: novoProjeto.instalador_cpf_cnpj,
        instalador_contato: novoProjeto.instalador_contato
      };
      // Sempre registrar quem criou o projeto (criador = qualquer usuário logado)
      if (user?.id) projetoPayload.vendedor_id = user.id;
      const { data, error } = await supabase
        .from('projetos')
        .insert(projetoPayload)
        .select()
        .single();

      if (error) {
        console.error('Erro ao cadastrar projeto:', error);
        toast.error('Erro ao cadastrar projeto: ' + error.message);
        return;
      }

      // Se houver equipamentos selecionados, salvar vínculos mesmo no modo "Apenas Salvar Projeto"
      if (equipamentosSelecionados.length > 0) {
        try {
          for (const equipamento of equipamentosSelecionados) {
            const { error: equipError } = await supabase
              .from('projeto_equipamentos')
              .insert({
                projeto_id: data.id,
                equipamento_id: parseInt(equipamento.equipamento_id),
                quantidade: equipamento.quantidade
              });

            if (equipError) {
              console.error('Erro ao salvar equipamento (apenas projeto):', equipError);
              toast.warning('Projeto salvo, mas ocorreu erro ao salvar alguns equipamentos.');
            }
          }
        } catch (equipEx) {
          console.error('Exceção ao salvar equipamentos:', equipEx);
        }
      }

      
      // Upload do arquivo de vistoria
      if (novoProjeto.vistoria_file) {
        console.log('Iniciando upload da vistoria...');
        const uploadSuccess = await uploadFile(novoProjeto.vistoria_file, 'vistoria', data.id);
        if (!uploadSuccess) {
          toast.error('Erro ao fazer upload da vistoria. O projeto foi salvo, mas o arquivo não pôde ser anexado. Tente novamente mais tarde.');
        } else {
          toast.success('Vistoria enviada com sucesso!');
        }
      }

      console.log('Projeto cadastrado com sucesso (sem orçamento):', data);
      toast.success(`Projeto cadastrado com sucesso!`);
      
      queryClient.invalidateQueries({ queryKey: ['projetos'] });
      queryClient.invalidateQueries({ queryKey: ['projeto-equipamentos'] });
      
      // Chamar onSuccess se fornecido
      if (onSuccess) {
        onSuccess();
      }
      
      // Redirecionar para a listagem de projetos
      window.dispatchEvent(new CustomEvent('mudar_secao', { detail: 'projetos' }));
      
      // Reset form
      setNovoProjeto({
        cliente_id: null,
        observacoes: "",
        valor_projeto: 0,
        empresa_responsavel_id: null,
        empresa_responsavel: "",
        cnpj_empresa: "",
        telefone_empresa: "",
        email_empresa: "",
        is_projeto_elevo: false,
        id_projeto_elevo: "",
        empresa_projeto_externo_id: "",
        consultor_id: "",
        consultor_nome: "",
        consultor_celular: "",
        consultor_cpf_cnpj: "",
        consultor_email: "",
        instalador_id: "",
        instalador_nome: "",
        instalador_cpf_cnpj: "",
        instalador_contato: "",
        vendedor_nome: "",
        vendedor_celular: "",
        vendedor_cpf_cnpj: "",
        vistoria_file: null
      });
      setEquipamentosSelecionados([]);
      
    } catch (error) {
      console.error('Erro inesperado:', error);
      toast.error('Erro inesperado ao cadastrar projeto');
    }
  };

  const handleSubmitComOrcamento = async () => {
    // Prevenir duplo clique - verificação dupla (estado + ref)
    if (isCreatingProject || isCreatingRef.current) {
      console.log('⚠️ Criação de projeto já em andamento, ignorando clique duplo');
      return;
    }

    if (!novoProjeto.cliente_id) {
      toast.error("Selecione um cliente");
      return;
    }

    if (equipamentosSelecionados.length === 0) {
      toast.error("Adicione pelo menos um equipamento ao projeto");
      return;
    }

    // Marcar como em criação (estado + ref)
    setIsCreatingProject(true);
    isCreatingRef.current = true;

    try {
      console.log('Iniciando criação de projeto com orçamento...');
      
      // 1. Criar o projeto primeiro
      const projetoPayloadComOrcamento: Record<string, unknown> = {
        cliente_id: novoProjeto.cliente_id,
        status: "orcamento", // Status padrão
        observacoes: novoProjeto.observacoes,
        valor_projeto: novoProjeto.valor_projeto,
        empresa_responsavel_id: novoProjeto.empresa_responsavel_id,
        empresa_projeto_externo_id: novoProjeto.empresa_projeto_externo_id || null,
        empresa_responsavel: novoProjeto.empresa_responsavel,
        cnpj_empresa: novoProjeto.cnpj_empresa,
        telefone_empresa: novoProjeto.telefone_empresa,
        email_empresa: novoProjeto.email_empresa,
        is_projeto_elevo: novoProjeto.is_projeto_elevo,
        id_projeto_elevo: novoProjeto.id_projeto_elevo,
        consultor_id: novoProjeto.consultor_id || null,
        consultor_nome: novoProjeto.consultor_nome || null,
        consultor_celular: novoProjeto.consultor_celular || null,
        consultor_cpf_cnpj: novoProjeto.consultor_cpf_cnpj || null,
        consultor_email: novoProjeto.consultor_email || null,
        instalador_nome: novoProjeto.instalador_nome,
        instalador_cpf_cnpj: novoProjeto.instalador_cpf_cnpj,
        instalador_contato: novoProjeto.instalador_contato
      };
      // Sempre registrar quem criou o projeto (criador = qualquer usuário logado)
      if (user?.id) projetoPayloadComOrcamento.vendedor_id = user.id;
      const { data: projetoData, error: projetoError } = await supabase
        .from('projetos')
        .insert(projetoPayloadComOrcamento)
        .select()
        .single();

      if (projetoError) {
        console.error('Erro ao cadastrar projeto:', projetoError);
        toast.error('Erro ao cadastrar projeto: ' + projetoError.message);
        // Resetar estado ANTES do return
        setIsCreatingProject(false);
        isCreatingRef.current = false;
        return;
      }

      console.log('Projeto criado com sucesso:', projetoData);

      // 2. Se há arquivo de vistoria, fazer upload
      if (novoProjeto.vistoria_file) {
        const uploadSuccess = await uploadFile(novoProjeto.vistoria_file, 'vistoria', projetoData.id);
        if (!uploadSuccess) {
          toast.error('Erro ao fazer upload da vistoria. Verifique as permissões do storage.');
        } else {
          toast.success('Vistoria enviada com sucesso!');
        }
      }

      // 3. Salvar equipamentos do projeto
      for (const equipamento of equipamentosSelecionados) {
        const { error: equipError } = await supabase
          .from('projeto_equipamentos')
          .insert({
            projeto_id: projetoData.id,
            equipamento_id: parseInt(equipamento.equipamento_id),
            quantidade: equipamento.quantidade
          });

        if (equipError) {
          console.error('Erro ao salvar equipamento:', equipError);
        }
      }

      // 4. Calcular quantidade de painéis
      const quantidadePaineis = equipamentosSelecionados
        .filter(eq => eq.tipo.toLowerCase() === 'painel')
        .reduce((total, eq) => total + eq.quantidade, 0);

      console.log('Quantidade de painéis calculada:', quantidadePaineis);

      // 5. Calcular valor total baseado nos equipamentos
      const valorTotal = equipamentosSelecionados.reduce((total, eq) => {
        const equipamentoOriginal = equipamentos.find(e => e.id.toString() === eq.equipamento_id);
        const preco = equipamentoOriginal?.preco_unitario || 0;
        return total + (preco * eq.quantidade);
      }, 0);

      console.log('Valor total calculado:', valorTotal);

      // 6. Preparar dados para o modal de seleção de plano
      const dadosProjeto = {
        projeto: projetoData,
        equipamentos: equipamentosSelecionados,
        valorTotal,
        quantidadePaineis,
        cliente_id: novoProjeto.cliente_id
      };

      console.log('Dados preparados para modal:', dadosProjeto);
      
      // 7. Mostrar modal de seleção de plano
      setProjetoParaOrcamento(dadosProjeto);
      setShowSelecionarPlano(true);
      
    } catch (error) {
      console.error('Erro inesperado:', error);
      toast.error('Erro inesperado ao cadastrar projeto');
      // Sempre resetar em caso de erro
      setIsCreatingProject(false);
      isCreatingRef.current = false;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Novo Projeto</h2>
        <p className="text-gray-600">Cadastre um projeto existente para gerar orçamento e controle de pedidos</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Seleção do Cliente */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <FolderPlus className="h-5 w-5 text-green-600" />
              Cliente do Projeto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <ClienteSearchSelect
                value={novoProjeto.cliente_id?.toString() || ""}
                onChange={(clienteId, clienteNome) => {
                  setNovoProjeto({...novoProjeto, cliente_id: parseInt(clienteId)});
                }}
                label="Cliente *"
              />
            </div>
          </CardContent>
        </Card>

        {/* Dados da Empresa Responsável */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Building className="h-5 w-5 text-green-600" />
                Dados da Empresa Responsável
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => empresaInputRef.current?.focus()}
                >
                  Selecionar empresa
                </Button>
                <Button 
                  type="button" 
                  onClick={preencherDadosElevo}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                  size="sm"
                >
                  <Zap className="h-4 w-4 mr-2" />
                  ELEVO
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <EmpresaSearchSelect
                ref={empresaInputRef}
                value={novoProjeto.empresa_responsavel_id?.toString() || ""}
                onChange={(empresaId, empresa) => {
                  const id = empresaId ? parseInt(empresaId) : null;
                  setNovoProjeto(prev => ({ ...prev, empresa_responsavel_id: id }));
                  if (empresa) aplicarEmpresaDoCadastro(empresa);
                }}
                label="Empresa (cadastro)"
              />
              {!!novoProjeto.empresa_responsavel_id && (
                <div className="mt-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const empresa = empresas.find(e => e.id === novoProjeto.empresa_responsavel_id);
                      if (empresa) {
                        aplicarEmpresaDoCadastro(empresa);
                        toast.success("Dados recarregados do cadastro da empresa!");
                      }
                    }}
                  >
                    Recarregar do cadastro
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label htmlFor="empresa_responsavel">Nome da Empresa</Label>
              <Input
                id="empresa_responsavel"
                value={novoProjeto.empresa_responsavel}
                onChange={(e) => setNovoProjeto({...novoProjeto, empresa_responsavel: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="cnpj_empresa">CNPJ</Label>
              <Input
                id="cnpj_empresa"
                value={novoProjeto.cnpj_empresa}
                onChange={(e) => setNovoProjeto({...novoProjeto, cnpj_empresa: e.target.value})}
                placeholder="00.000.000/0001-00"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="telefone_empresa">Telefone</Label>
              <Input
                id="telefone_empresa"
                value={novoProjeto.telefone_empresa}
                onChange={(e) => setNovoProjeto({...novoProjeto, telefone_empresa: e.target.value})}
                placeholder="(11) 3456-7890"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email_empresa">E-mail</Label>
              <Input
                id="email_empresa"
                type="email"
                value={novoProjeto.email_empresa}
                onChange={(e) => setNovoProjeto({...novoProjeto, email_empresa: e.target.value})}
                placeholder="contato@empresa.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="valor_projeto">Valor do Projeto *</Label>
              <Input
                id="valor_projeto"
                type="text"
                value={formatarMoeda(novoProjeto.valor_projeto)}
                onChange={(e) => {
                  const valorComMascara = aplicarMascaraMoeda(e.target.value);
                  const valorNumerico = converterMoedaParaNumero(valorComMascara);
                  setNovoProjeto({...novoProjeto, valor_projeto: valorNumerico});
                }}
                placeholder="R$ 0,00"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="empresa_projeto_externo_id">ID do projeto na empresa</Label>
              <Input
                id="empresa_projeto_externo_id"
                value={novoProjeto.empresa_projeto_externo_id}
                onChange={(e) => {
                  const valorLimpo = e.target.value.replace(/\D/g, '');
                  setNovoProjeto(prev => ({
                    ...prev,
                    empresa_projeto_externo_id: valorLimpo,
                    // Compatibilidade: se for Elevo, manter campo legado atualizado
                    id_projeto_elevo: prev.is_projeto_elevo ? valorLimpo : prev.id_projeto_elevo
                  }));
                }}
                placeholder="168167"
                className="mt-1"
                disabled={!novoProjeto.empresa_responsavel_id && !novoProjeto.empresa_responsavel}
              />
            </div>
          </CardContent>
        </Card>

        {/* Consultor Responsável */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-900">Consultor Responsável</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <ConsultorSearchSelect
                value={novoProjeto.consultor_id || ""}
                onChange={(id, nome, celular, cpfCnpj, email) => {
                  setNovoProjeto({
                    ...novoProjeto,
                    consultor_id: id,
                    consultor_nome: nome,
                    consultor_celular: celular,
                    consultor_cpf_cnpj: cpfCnpj,
                    consultor_email: email
                  });
                }}
                label="Selecionar Consultor"
              />
            </div>
            <div>
              <Label>Nome</Label>
              <Input value={novoProjeto.consultor_nome} onChange={(e) => setNovoProjeto({...novoProjeto, consultor_nome: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>Celular</Label>
              <Input value={novoProjeto.consultor_celular} onChange={(e) => setNovoProjeto({...novoProjeto, consultor_celular: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>CPF/CNPJ</Label>
              <Input value={novoProjeto.consultor_cpf_cnpj} onChange={(e) => setNovoProjeto({...novoProjeto, consultor_cpf_cnpj: e.target.value})} className="mt-1" />
            </div>
            <div>
              <Label>E-mail</Label>
              <Input value={novoProjeto.consultor_email} onChange={(e) => setNovoProjeto({...novoProjeto, consultor_email: e.target.value})} className="mt-1" />
            </div>
          </CardContent>
        </Card>

        {/* Equipamentos do Projeto */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <Zap className="h-5 w-5 text-blue-600" />
              Equipamentos do Projeto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Seleção de equipamentos */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg bg-gray-50">
              <div>
                <Label>Tipo de Equipamento</Label>
                <Select value={novoEquipamento.tipo} onValueChange={(value) => {
                  setNovoEquipamento({...novoEquipamento, tipo: value, equipamento_id: ""});
                }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inversor">Inversor</SelectItem>
                    <SelectItem value="painel">Painel</SelectItem>
                    <SelectItem value="estrutura">Estrutura</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Quantidade</Label>
                <Input
                  type="number"
                  min="1"
                  value={novoEquipamento.quantidade}
                  onChange={(e) => setNovoEquipamento({...novoEquipamento, quantidade: parseInt(e.target.value) || 1})}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Equipamento</Label>
                <Select 
                  value={novoEquipamento.equipamento_id} 
                  onValueChange={(value) => setNovoEquipamento({...novoEquipamento, equipamento_id: value})}
                  disabled={!novoEquipamento.tipo}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione o modelo" />
                  </SelectTrigger>
                  <SelectContent>
                    {equipamentos.filter(eq => !novoEquipamento.tipo || eq.tipo.toLowerCase() === novoEquipamento.tipo.toLowerCase()).map((equipamento) => (
                      <SelectItem key={equipamento.id} value={equipamento.id.toString()}>
                        {equipamento.marca} {equipamento.modelo} {equipamento.potencia && `- ${equipamento.potencia}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-3 flex justify-end">
                <Button type="button" onClick={adicionarEquipamento} size="sm">
                  <Plus className="h-4 w-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </div>

            {/* Lista de equipamentos selecionados */}
            {equipamentosSelecionados.length > 0 && (
              <div className="space-y-2">
                <Label>Equipamentos Selecionados:</Label>
                {equipamentosSelecionados.map((item, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg bg-white">
                    <div className="flex-1">
                      <span className="font-medium">{item.tipo.toUpperCase()}</span> - 
                      <span className="ml-2">{item.equipamento?.marca} {item.equipamento?.modelo}</span>
                      {item.equipamento?.potencia && <span className="text-gray-500"> - {item.equipamento.potencia}</span>}
                      <span className="ml-2 text-blue-600">Qtd: {item.quantidade}</span>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removerEquipamento(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dados do Vendedor */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <User className="h-5 w-5 text-green-600" />
                Dados do Vendedor (Opcional)
              </CardTitle>
              {currentUser && (
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setNovoProjeto(prev => ({
                      ...prev,
                      vendedor_nome: currentUser.nome || "",
                      vendedor_celular: currentUser.telefone || "",
                      vendedor_cpf_cnpj: ""
                    }));
                    toast.success("Dados do vendedor atualizados com seus dados!");
                  }}
                  className="text-blue-600 border-blue-300 hover:bg-blue-50"
                >
                  <User className="h-4 w-4 mr-1" />
                  Usar Meus Dados
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="vendedor_nome">
                Nome
                {currentUser && novoProjeto.vendedor_nome === currentUser.nome && (
                  <span className="ml-2 text-xs text-green-600 font-normal">(Preenchido automaticamente)</span>
                )}
              </Label>
              <Input
                id="vendedor_nome"
                value={novoProjeto.vendedor_nome}
                onChange={(e) => setNovoProjeto({...novoProjeto, vendedor_nome: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="vendedor_celular">
                Celular
                {currentUser && novoProjeto.vendedor_celular === currentUser.telefone && (
                  <span className="ml-2 text-xs text-green-600 font-normal">(Preenchido automaticamente)</span>
                )}
              </Label>
              <Input
                id="vendedor_celular"
                value={novoProjeto.vendedor_celular}
                onChange={(e) => setNovoProjeto({...novoProjeto, vendedor_celular: e.target.value})}
                placeholder="(11) 99999-9999"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="vendedor_cpf_cnpj">CPF/CNPJ</Label>
              <Input
                id="vendedor_cpf_cnpj"
                value={novoProjeto.vendedor_cpf_cnpj}
                onChange={(e) => setNovoProjeto({...novoProjeto, vendedor_cpf_cnpj: e.target.value})}
                placeholder="000.000.000-00"
                className="mt-1"
              />
            </div>
          </CardContent>
        </Card>

        {/* Dados do Instalador */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <Wrench className="h-5 w-5 text-green-600" />
              Dados do Instalador
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Seleção do Instalador */}
            <InstaladorSearchSelect
              value={novoProjeto.instalador_id || ""}
              onChange={(instaladorId, instaladorNome, instaladorContato, instaladorCpfCnpj) => {
                setNovoProjeto({
                  ...novoProjeto,
                  instalador_id: instaladorId,
                  instalador_nome: instaladorNome,
                  instalador_contato: instaladorContato,
                  instalador_cpf_cnpj: instaladorCpfCnpj
                });
              }}
              label="Selecionar Instalador"
            />

            {/* Campos de dados do instalador */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="instalador_nome">Nome do Instalador</Label>
                <Input
                  id="instalador_nome"
                  value={novoProjeto.instalador_nome}
                  onChange={(e) => setNovoProjeto({...novoProjeto, instalador_nome: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="instalador_contato">Celular</Label>
                <Input
                  id="instalador_contato"
                  value={novoProjeto.instalador_contato}
                  onChange={(e) => setNovoProjeto({...novoProjeto, instalador_contato: e.target.value})}
                  placeholder="(11) 99999-9999"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="instalador_cpf_cnpj">CPF/CNPJ</Label>
                <Input
                  id="instalador_cpf_cnpj"
                  value={novoProjeto.instalador_cpf_cnpj}
                  onChange={(e) => setNovoProjeto({...novoProjeto, instalador_cpf_cnpj: e.target.value})}
                  placeholder="000.000.000-00"
                  className="mt-1"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Documentos */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <FileText className="h-5 w-5 text-green-600" />
              Documentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div>
              <FileInput
                label="Vistoria"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onFileSelect={(file) => {
                  if (file) {
                    handleFileUpload({ target: { files: [file] } } as any, 'vistoria');
                  }
                }}
                selectedFile={novoProjeto.vistoria_file}
                placeholder="Anexar Vistoria"
              />
            </div>
          </CardContent>
        </Card>

        {/* Observações */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-gray-900">Observações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Textarea
              value={novoProjeto.observacoes}
              onChange={(e) =>
                setNovoProjeto({
                  ...novoProjeto,
                  observacoes: e.target.value.slice(0, MAX_OBSERVACOES_PROPOSTA),
                })
              }
              placeholder="Observações adicionais sobre o projeto (aparecem na proposta PDF)..."
              rows={4}
              maxLength={MAX_OBSERVACOES_PROPOSTA}
            />
            <p className="text-xs text-muted-foreground text-right">
              {novoProjeto.observacoes.length}/{MAX_OBSERVACOES_PROPOSTA}
            </p>
          </CardContent>
        </Card>

        {/* Botões de Ação */}
        <div className="flex justify-between items-center gap-4">
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline"
              onClick={handleSubmitComOrcamento}
              disabled={!novoProjeto.cliente_id || isCreatingProject || showSelecionarPlano}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {isCreatingProject ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Criando Projeto...
                </>
              ) : (
                "Salvar Projeto e Gerar Orçamento"
              )}
            </Button>
          </div>
          
          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline"
            >
              Cancelar
            </Button>
            <Button type="submit" className="bg-primary hover:bg-primary/90 text-primary-foreground px-8">
              Apenas Salvar Projeto
            </Button>
          </div>
        </div>
      </form>

      {/* Modal de Seleção de Plano */}
      <SelecionarPlanoDialog
        open={showSelecionarPlano}
        onOpenChange={(open) => {
          setShowSelecionarPlano(open);
          if (!open) {
            // Ao fechar o modal (cancelar), liberar o botão
            setIsCreatingProject(false);
            isCreatingRef.current = false;
          }
        }}
        onConfirm={confirmarPlano}
        valorBase={projetoParaOrcamento?.valorTotal || 0}
        quantidadePainels={projetoParaOrcamento?.quantidadePaineis || 0}
      />
    </div>
  );
}